<template>
  <MarketingHeader>
    <div class="bento-item" role="region" aria-label="Réglages du marketing">
      <div class="head"><h3>Réglages</h3></div>

      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <h4 class="panel-title">Répartition visée entre vos contenus</h4>
        <p class="hint">La part de vos contenus que vous voulez consacrer à chaque intention. Elle sert de repère sur l'écran Contenus : rien ne vous est imposé.</p>
        <div class="objective">
          <CustomTextField v-for="stage in stages" :key="stage.value" :content=inputs[stage.key] />
          <div class="total" :class="{wrong: total !== 100}">
            <span class="total-label">Total</span>
            <strong>{{ total }} %</strong>
          </div>
        </div>
        <div class="preview" aria-hidden="true">
          <span v-for="stage in stages" :key="stage.value" v-show="share(stage) > 0" class="segment" :style="{width: share(stage) + '%', backgroundColor: stage.color, color: stage.textColor}">{{ share(stage) >= 12 ? share(stage) + ' %' : '' }}</span>
        </div>
        <p v-if="total !== 100" class="problem">Le total doit faire 100 % (il fait {{ total }} %).</p>
        <button type="button" class="panel-button reset" @click="resetObjective">Revenir à 50 / 30 / 20</button>

        <h4 class="panel-title">Qui vous êtes et à qui vous parlez</h4>
        <CustomTextAreaField :content=inputs.writingBrief :auto-grow="true" :min-height="130" />
        <p class="hint">Ce texte est placé en tête de chaque prompt copié depuis une publication : votre activité, votre public, votre ton, vos règles de hashtags. {{ briefCount }} / {{ MAX_BRIEF }} caractères.</p>

        <h4 class="panel-title">Structure de texte par défaut</h4>
        <div class="chips" role="group" aria-label="Structure de texte par défaut">
          <button type="button" class="chip" :class="{selected: !inputs.defaultPromptTemplateId.value}" :aria-pressed="!inputs.defaultPromptTemplateId.value" @click="inputs.defaultPromptTemplateId.value = ''">Selon l'intention</button>
          <button v-for="template in templates" :key="template.id" type="button" class="chip" :class="{selected: inputs.defaultPromptTemplateId.value === template.id}"
                  :aria-pressed="inputs.defaultPromptTemplateId.value === template.id" :title="template.description" @click="inputs.defaultPromptTemplateId.value = template.id">{{ template.label }}</button>
        </div>
        <p class="hint">« Selon l'intention » propose la structure conseillée pour chaque contenu ; vous pouvez en changer sur chaque publication.</p>

        <p class="dirty" role="status">{{ isDirty ? 'Modifications non enregistrées' : '' }}</p>
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=buttons.submit />
        </div>
      </CustomForm>
    </div>
  </MarketingHeader>
</template>

<script>
import MarketingHeader from "@/marketing/components/MarketingHeader.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomTextAreaField from "@/components/forms/customs/customTextAreaField/CustomTextAreaField.vue";
import {NotificationService} from "@vues/v-common";
import {SettingsController} from "@/marketing/controllers/SettingsController";
import {PromptController} from "@/marketing/controllers/PromptController";
import {FUNNEL_STAGE_ENUM} from "@/marketing/utils";

const MAX_BRIEF = 5000;
const DEFAULT_OBJECTIVE = {tofu: 50, mofu: 30, bofu: 20};

/**
 * Écran « Réglages » du groupe : répartition visée entre les trois intentions, présentation placée en tête des prompts, structure par défaut.
 */
export default {
  name: 'SettingsView',
  components: {MarketingHeader, CustomForm, CustomButton, CustomTextField, CustomTextAreaField},
  data() {
    return {
      MAX_BRIEF,
      stages: Object.values(FUNNEL_STAGE_ENUM),
      templates: [],
      saved: '',
      inputs: {
        tofu: {label: FUNNEL_STAGE_ENUM.TOFU.label + ' (%)', value: DEFAULT_OBJECTIVE.tofu, type: 'number', step: 1},
        mofu: {label: FUNNEL_STAGE_ENUM.MOFU.label + ' (%)', value: DEFAULT_OBJECTIVE.mofu, type: 'number', step: 1},
        bofu: {label: FUNNEL_STAGE_ENUM.BOFU.label + ' (%)', value: DEFAULT_OBJECTIVE.bofu, type: 'number', step: 1},
        writingBrief: {label: 'Présentation', value: '', type: 'text'},
        defaultPromptTemplateId: {value: ''},
      },
      buttons: {
        submit: {text: "Enregistrer"},
      }
    };
  },
  computed: {
    total() {
      return this.stages.reduce((sum, stage) => sum + this.percentOf(stage), 0);
    },
    briefCount() {
      return Array.from(String(this.inputs.writingBrief.value ?? '')).length;
    },
    isDirty() {
      return this.saved !== '' && this.snapshot() !== this.saved;
    },
  },
  async mounted() {
    const [settings, templates] = await Promise.all([SettingsController.get(), PromptController.getTemplates()]);
    if (Array.isArray(templates)) this.templates = templates;
    if (settings && !settings.errors) this.fill(settings);
  },
  methods: {
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    percentOf(stage) {
      const value = Number(this.inputs[stage.key].value);
      return Number.isFinite(value) ? value : 0;
    },
    // Largeur de la barre d'aperçu : la part de chaque intention dans le total saisi
    share(stage) {
      return this.total > 0 ? Math.round(this.percentOf(stage) * 100 / this.total) : 0;
    },
    snapshot() {
      return JSON.stringify([this.inputs.tofu.value, this.inputs.mofu.value, this.inputs.bofu.value, this.inputs.writingBrief.value, this.inputs.defaultPromptTemplateId.value]);
    },
    fill(settings) {
      const objective = settings.funnelObjective ?? DEFAULT_OBJECTIVE;
      this.inputs.tofu.value = objective.tofu;
      this.inputs.mofu.value = objective.mofu;
      this.inputs.bofu.value = objective.bofu;
      this.inputs.writingBrief.value = settings.writingBrief ?? '';
      this.inputs.defaultPromptTemplateId.value = settings.defaultPromptTemplateId ?? '';
      this.saved = this.snapshot();
    },
    resetObjective() {
      this.inputs.tofu.value = DEFAULT_OBJECTIVE.tofu;
      this.inputs.mofu.value = DEFAULT_OBJECTIVE.mofu;
      this.inputs.bofu.value = DEFAULT_OBJECTIVE.bofu;
    },
    async submitForm(event) {
      this.preventNativeSubmit(event);

      const objective = {tofu: this.percentOf(FUNNEL_STAGE_ENUM.TOFU), mofu: this.percentOf(FUNNEL_STAGE_ENUM.MOFU), bofu: this.percentOf(FUNNEL_STAGE_ENUM.BOFU)};
      if (Object.values(objective).some(value => !Number.isInteger(value) || value < 0) || this.total !== 100) {
        (new NotificationService).addNotification("danger", "La répartition doit être faite de nombres entiers qui totalisent 100 %");
        return;
      }

      const body = {
        funnelObjective: objective,
        writingBrief: String(this.inputs.writingBrief.value ?? '').trim(),
        defaultPromptTemplateId: this.inputs.defaultPromptTemplateId.value || null,
      };
      const res = await this.$refs.form.submitForm(() => SettingsController.patch(body));
      if (!res || res.errors) return;

      (new NotificationService).addNotification("success", "Réglages enregistrés");
      const settings = await SettingsController.get();
      if (settings && !settings.errors) this.fill(settings);
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/marketing/style/modal.styl';

.head h3
  margin 0 0 8px
  font-size 1.05em
  text-transform uppercase
  color THEME_COLOR_PRIMARY

.panel-title
  margin-top 18px

.objective
  display grid
  grid-template-columns repeat(3, minmax(0, 1fr)) 120px
  gap 4px 18px
  align-items end
  max-width 760px

.total
  display flex
  flex-direction column
  justify-content flex-end
  height CTRL_HEIGHT + 24px
  padding-bottom 8px
  color THEME_COLOR_PRIMARY

  .total-label
    color CTRL_TEXT_MUTED
    font-size 0.82em
    font-weight 600

  strong
    font-size 1.4em

  &.wrong strong
    color CTRL_ERROR

.preview
  display flex
  height 28px
  max-width 760px
  margin 10px 0 4px
  overflow hidden
  border 1px solid CTRL_BORDER
  border-radius 10px

  .segment
    display flex
    align-items center
    justify-content center
    overflow hidden
    font-size 13px
    font-weight 700
    white-space nowrap

.problem
  margin 4px 2px
  color CTRL_ERROR
  font-size 0.9em
  font-weight 600

// Le formulaire est une colonne flex : sans align-self le bouton prendrait toute la largeur
.reset
  align-self flex-start
  height 34px
  margin-top 6px

.button-container
  margin-top 22px
  max-width 760px

// Ligne réservée : la page ne bouge pas quand l'indicateur apparaît
.dirty
  min-height 1.3em
  margin 14px 0 -8px
  max-width 760px
  color #b45309
  font-size 0.85em
  font-weight 600

.hint
  max-width 760px

@media (max-width: 760px)
  .objective
    grid-template-columns 1fr
</style>
